import {
  Badge,
  BetaCardBody,
  BetaCardRootContainer,
  Button,
  Heading,
  HeadingLevel,
  IconButton,
  Link,
  ServiceIcon,
  Text,
  Typography,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";
import { getServiceIconUrl } from "@zapier/utils";
import { MANAGED_CONNECTIONS_HELP_URL } from "../constants";
import styles from "./ManagedConnectionNudgeCard.module.css";

const CHECKLIST_ITEM_KEYS = [
  "members-leave",
  "credentials-admin-control",
  "one-audited-connection",
] as const;

// Static, hardcoded quick-pick list per the Figma mock - no per-account/top-apps
// logic. Order matters: it's the order the icons render in. `slug` is the
// real zapier.com/apps/<slug> directory slug (verified against
// zapier.com/generated/<slug>/64, not guessed) - required for
// getServiceIconUrl to resolve a real icon instead of the grey placeholder.
export const QUICK_PICK_APPS = [
  { name: "HubSpot", slug: "hubspot" },
  { name: "Salesforce", slug: "salesforce" },
  { name: "Amazon Bedrock", slug: "amazon-aws-bedrock" },
  { name: "Jira", slug: "jira-software-cloud" },
] as const;

export interface ManagedConnectionNudgeCardProps {
  onCreateManagedConnection: () => void;
  onDismiss: () => void;
}

export function ManagedConnectionNudgeCard({
  onCreateManagedConnection,
  onDismiss,
}: ManagedConnectionNudgeCardProps) {
  const t = useTranslations("managed-connection-nudge-card");

  return (
    <BetaCardRootContainer
      variant="outlined"
      data-custom-component="connections::managed-connection-nudge-card"
    >
      <BetaCardBody>
        <div className={styles.card}>
          <IconButton
            name="formX"
            aria-label={t("accessibility.dismiss")}
            size="small"
            variant="ghost"
            className={styles.dismissButton}
            onPress={onDismiss}
          />
          <Badge variant="primary-stronger" className={styles.badge}>
            {t("badge.recommended")}
          </Badge>
          <div className={styles.root}>
            <div className={styles.main}>
              <div className={styles.titleGroup}>
                <HeadingLevel>
                  <Heading style={Typography.ParagraphHeader3}>
                    {t("headings.title")}
                  </Heading>
                </HeadingLevel>
                <Text type="SmallPrint1Multiline" tag="p">
                  {t("messages.body")}
                </Text>
              </div>
              <ul className={styles.checklist}>
                {CHECKLIST_ITEM_KEYS.map((key) => (
                  <li key={key} className={styles.checklistItem}>
                    <Icon
                      aria-hidden={true}
                      name="formCheckCircle"
                      size={16}
                      color="PrimeGreen"
                    />
                    <Text type="SmallPrint1Multiline" tag="span">
                      {t(`checklist.${key}`)}
                    </Text>
                  </li>
                ))}
              </ul>
              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  size="medium"
                  onPress={onCreateManagedConnection}
                >
                  {t("buttons.create-managed-connection")}
                </Button>
                <Link
                  href={MANAGED_CONNECTIONS_HELP_URL}
                  target="_blank"
                  variant="tertiary"
                  size="medium"
                >
                  {t("buttons.how-it-works")}
                </Link>
              </div>
            </div>
            <div className={styles.quickPick}>
              <Text type="MinimalPrint1Multiline" color="TextWeaker" tag="p">
                {t("quick-pick.label")}
              </Text>
              <div className={styles.quickPickList}>
                {QUICK_PICK_APPS.map((app) => (
                  <BetaCardRootContainer
                    key={app.slug}
                    variant="outlined"
                    padding="small"
                    className={styles.quickPickCard}
                  >
                    <BetaCardBody>
                      <div className={styles.quickPickRow}>
                        <ServiceIcon
                          serviceName={app.name}
                          size="small"
                          src={getServiceIconUrl(app.slug)}
                        />
                        <Text
                          type="Body3"
                          tag="span"
                          className={styles.quickPickAppName}
                        >
                          {app.name}
                        </Text>
                      </div>
                    </BetaCardBody>
                  </BetaCardRootContainer>
                ))}
              </div>
            </div>
          </div>
        </div>
      </BetaCardBody>
    </BetaCardRootContainer>
  );
}
